import { Box, Button, ButtonProps, Dialog } from "@mui/material";
import Script from "next/script";
import { useLayoutEffect, useState } from "react";
import { ulid } from "ulid";

import { useBoolean } from "../../hooks/useBoolean";
import { useDeepEqual } from "../../hooks/useDeepEqual";
import { useEventCallback } from "../../hooks/useEventCallback";
import { Event, logEvent } from "../../logging/logEvent";
import { IVouchedConfig, loadVouched } from "./loadVouched";

interface IProps {
    buttonProps?: Omit<ButtonProps, "onClick">;
    vouchedVerification?: IVouchedConfig.IVerification;

    onSuccess(jobId: string): void;
    onFail?(errors: IVouchedConfig.IVerificationError[]): void;
}

export const VouchedButton: React.FC<React.PropsWithChildren<IProps>> = ({ children, ...props }) => {
    const { buttonProps } = props;
    const { loading } = buttonProps ?? {};

    const [isScriptLoaded, , { setTrue: setScriptLoaded }] = useBoolean();
    const [isDialogOpen, , { setTrue: openDialog, setFalse: closeDialog }] = useBoolean();

    return (
        <>
            <Script src="https://static.vouched.id/widget/vouched-2.0.0.js" onReady={setScriptLoaded} />
            <Button {...buttonProps} loading={loading || !isScriptLoaded} onClick={openDialog}>
                {children ?? "Upload Selfie"}
            </Button>
            <Dialog
                fullWidth={true}
                maxWidth="xl"
                open={isDialogOpen}
                onClose={closeDialog}
                sx={{ "& .MuiDialog-paper": { height: "100%" } }}
            >
                <VouchedDialogContent {...props} onClose={closeDialog} />
            </Dialog>
        </>
    );
};

interface IVouchedDialogContentProps extends IProps {
    onClose(): void;
}

const VouchedDialogContent: React.FC<IVouchedDialogContentProps> = ({
    vouchedVerification: rawVerification,
    onSuccess,
    onFail,
    onClose,
}) => {
    const [elementId] = useState(`vouched-container-${ulid()}`);

    const handleDone = useEventCallback(
        ({ id: jobId, status, result: { success }, errors }: IVouchedConfig.IJob): void => {
            logEvent(Event.SelfieVerificationDone, {
                jobId,
                status,
                success,
                errors: errors.map(({ type, message }) => ({ type, message })),
            });

            if (status === "completed" && success) {
                onSuccess(jobId);
                onClose();
            } else {
                onFail?.(errors);
            }
        },
    );

    const vouchedVerification = useDeepEqual(rawVerification);
    useLayoutEffect(() => {
        return loadVouched(elementId, { verification: vouchedVerification, onDone: handleDone });
    }, [elementId, vouchedVerification, handleDone]);

    return <Box id={elementId} height="100%" />;
};
